export function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export function normalizeSearch(value) {
  return String(value || '').toLowerCase().trim();
}

export function normalizeRate(value) {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0;
  }

  // Product setup stores commissions as percent values (0-100), so 1 means 1%.
  // Always divide by 100 — the old "> 1" heuristic misread 1 as 100%.
  return parsed / 100;
}

export function formatCurrency(value) {
  const amount = Number(value || 0);
  return `PHP ${amount.toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatDate(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function matchesDateRange(dateValue, dateFrom, dateTo) {
  if (!dateFrom && !dateTo) {
    return true;
  }
  if (!dateValue) {
    return false;
  }

  const rowDate = new Date(dateValue);
  if (Number.isNaN(rowDate.getTime())) {
    return false;
  }

  const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
  const to = dateTo ? new Date(`${dateTo}T23:59:59`) : null;

  if (from && rowDate < from) {
    return false;
  }
  if (to && rowDate > to) {
    return false;
  }

  return true;
}

export function resolveRoleLabel(role) {
  const normalizedRole = String(role || '').trim().toUpperCase();
  const labels = {
    SA: 'Sales Advisor',
    UM: 'Unit Manager',
    BM: 'Branch Manager',
    AAH: 'Assistant Area Head',
    DH: 'Division Head',
    AH: 'Area Head',
    SAH: 'Senior Area Head',
    CEO: 'CEO',
  };

  return labels[normalizedRole] || String(role || '');
}

export function resolvePositionField(positionValue) {
  const map = {
    SA: 'sa_commission',
    UM: 'um_commission',
    BM: 'bm_commission',
    AAH: 'aah_commission',
    DH: 'dh_commission',
    AH: 'ah_commission',
    SAH: 'sah_commission',
    CEO: 'ceo_commission',
    'Sales Advisor': 'sa_commission',
    'Unit Manager': 'um_commission',
    'Branch Manager': 'bm_commission',
    'Assistant Area Head': 'aah_commission',
    'Division Head': 'dh_commission',
    'Area Head': 'ah_commission',
    'Senior Area Head': 'sah_commission',
  };

  const normalized = String(positionValue || '').trim();
  if (!normalized) {
    return null;
  }

  if (normalized === 'CEO') {
    return 'ceo_commission';
  }

  if (map[normalized]) {
    return map[normalized];
  }

  const upper = normalized.toUpperCase();
  return map[upper] || null;
}

export function computeCommission(premiumAmount, rate) {
  const premium = Number(premiumAmount || 0);
  const netCommissionable = premium > 0 ? premium / 1.12 : 0;
  const vat = netCommissionable * 0.12;
  const grossCommission = netCommissionable * rate;
  const wtax = grossCommission * 0.1;
  const netCommission = grossCommission - wtax;

  return {
    premium,
    vat,
    netCommissionable,
    grossCommission,
    wtax,
    netCommission,
  };
}

export function resolveCommissionStatus(row) {
  const agentStatus = String(row?.agent_commission_status || '').trim();
  const commissionStatus = String(row?.commission_status || '').trim();

  if (agentStatus === 'Released' || commissionStatus === 'Released') {
    return 'Released';
  }

  if (agentStatus) {
    return agentStatus;
  }

  if (commissionStatus) {
    return commissionStatus;
  }

  return 'Pending';
}

export function triggerCsvDownload(filename, headers, rows) {
  const content = [headers, ...rows]
    .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function triggerExcelLikeDownload(filename, headers, rows) {
  const content = [headers, ...rows]
    .map((row) => row.map((cell) => String(cell ?? '').replace(/\t/g, ' ')).join('\t'))
    .join('\n');

  const blob = new Blob([content], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadTextFile(filename, content) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
